// lapse-prevention-software.js - Tier 3 Solution Page
export const content = {
  heroSubtitle: "Every lapse is preventable with the right warning system. Catch clients before they lose coverage, protect them from gaps, and preserve your commission.",

  problemSection: {
    title: "The Lapse Problem",
    html: `
      <p>Policy lapses are different from intentional switching. When a client switches plans, at least they still have coverage—even if it's with another agent. When a policy lapses, your client has no coverage at all. That's a failure for everyone.</p>

      <p>Lapses happen for many reasons: payment issues, confusion about paperwork, life disruptions, forgotten deadlines. But they share one thing in common: they're almost always preventable if you catch them in time.</p>

      <p>Lapse prevention software gives you visibility into clients at risk of losing coverage before it happens. You can intervene, help resolve the issue, and keep them protected—and keep your commission.</p>
    `
  },

  features: [
    {
      icon: "bell",
      title: "Early Warning Alerts",
      description: "System identifies clients showing lapse risk signals: payment issues, non-response to carrier communications, approaching grace period deadlines."
    },
    {
      icon: "clock",
      title: "Grace Period Tracking",
      description: "Know exactly where each client is in their payment status. Days remaining in grace period. Urgency level for intervention."
    },
    {
      icon: "users",
      title: "Intervention Workflows",
      description: "Automatic outreach sequences when lapse risk is detected. Escalating urgency as deadlines approach. Scripts for having the payment conversation."
    },
    {
      icon: "shield",
      title: "Reinstatement Support",
      description: "If a lapse does occur, tools to help with reinstatement process. Carrier contact information, timeline requirements, documentation needed."
    },
    {
      icon: "chart",
      title: "Lapse Analytics",
      description: "Track lapse rates across your book. Identify patterns: which clients, which plans, what causes. Use data to improve prevention."
    },
    {
      icon: "zap",
      title: "Payment Assistance Resources",
      description: "Information about programs that can help: LIS/Extra Help for Part D, MSP programs, state assistance programs. Help clients find solutions."
    }
  ],

  featuresTitle: "Preventing Coverage Gaps",

  mainContent: `
    <h2>Why Lapses Happen</h2>

    <p>Understanding lapse causes helps you prevent them:</p>

    <h3>Payment-Related</h3>
    <ul>
      <li><strong>Financial hardship:</strong> Client can't afford the premium</li>
      <li><strong>Payment method issues:</strong> Card expired, bank account changed</li>
      <li><strong>Forgot to set up auto-pay:</strong> Manual payments missed</li>
      <li><strong>Confusion about billing:</strong> Didn't understand when/how to pay</li>
    </ul>

    <h3>Administrative</h3>
    <ul>
      <li><strong>Paperwork confusion:</strong> Didn't understand what was required</li>
      <li><strong>Missed deadlines:</strong> Lost track of due dates</li>
      <li><strong>Address issues:</strong> Moved and didn't update, missed notices</li>
      <li><strong>Carrier communication failures:</strong> Notices not received or not understood</li>
    </ul>

    <h3>Life Disruptions</h3>
    <ul>
      <li><strong>Hospitalization:</strong> Client unable to manage during illness</li>
      <li><strong>Cognitive decline:</strong> Forgetting to handle paperwork</li>
      <li><strong>Family crisis:</strong> Coverage management fell off radar</li>
      <li><strong>Caregiver change:</strong> Person who handled things no longer involved</li>
    </ul>

    <h3>Intentional (But Mistaken)</h3>
    <ul>
      <li><strong>Thought they didn't need coverage:</strong> Didn't understand consequences</li>
      <li><strong>Planned to switch but didn't complete:</strong> Fell into gap</li>
      <li><strong>Confused about coverage status:</strong> Thought they were covered elsewhere</li>
    </ul>

    <h2>Warning Signs to Monitor</h2>

    <p>Pro IRP monitors for signals that indicate lapse risk:</p>

    <h3>Direct Signals</h3>
    <ul>
      <li>Grace period entry (payment delinquent)</li>
      <li>Premium assistance inquiry</li>
      <li>Carrier termination warning received</li>
      <li>Returned mail / invalid address</li>
    </ul>

    <h3>Indirect Signals</h3>
    <ul>
      <li>No contact response for extended period</li>
      <li>Financial stress mentioned in conversations</li>
      <li>Major life event (spouse death, hospitalization)</li>
      <li>Cognitive concerns observed</li>
      <li>Caregiver involvement changes</li>
    </ul>

    <h2>The Intervention Process</h2>

    <h3>Early Stage (First Warning)</h3>
    <p>When lapse risk is first detected:</p>
    <ul>
      <li>Reach out to understand the situation</li>
      <li>Determine the cause</li>
      <li>Assess whether it's resolvable</li>
      <li>Provide information about options</li>
    </ul>

    <p><em>"I wanted to check in because I saw there might be an issue with your coverage. Is everything okay? What can I help with?"</em></p>

    <h3>Urgent Stage (Grace Period Active)</h3>
    <p>When payment grace period is underway:</p>
    <ul>
      <li>Explain urgency and timeline</li>
      <li>Identify the specific barrier</li>
      <li>Provide concrete solutions</li>
      <li>Offer to help with payment process</li>
    </ul>

    <p><em>"Your coverage is in a grace period, which means you have [X] days to resolve this. If we don't address it, you'll lose coverage on [date]. Let's figure out what's going on and how to fix it."</em></p>

    <h3>Critical Stage (Days Remaining)</h3>
    <p>When termination is imminent:</p>
    <ul>
      <li>Direct, urgent communication</li>
      <li>Three-way calls with carrier if needed</li>
      <li>Same-day payment facilitation</li>
      <li>Explore any emergency options</li>
    </ul>

    <h2>Financial Assistance Resources</h2>

    <p>Many lapses happen because clients can't afford premiums. Know the resources:</p>

    <h3>Medicare Savings Programs (MSPs)</h3>
    <ul>
      <li><strong>QMB:</strong> Pays Part A and B premiums + deductibles</li>
      <li><strong>SLMB:</strong> Pays Part B premium</li>
      <li><strong>QI:</strong> Pays Part B premium</li>
      <li>Eligibility based on income/assets</li>
    </ul>

    <h3>Low-Income Subsidy (Extra Help)</h3>
    <ul>
      <li>Helps pay Part D premiums, deductibles, copays</li>
      <li>Significant savings for qualifying clients</li>
      <li>Many eligible people don't know about it</li>
    </ul>

    <h3>State and Local Programs</h3>
    <ul>
      <li>SHIP (State Health Insurance Assistance Program)</li>
      <li>AAA (Area Agency on Aging) resources</li>
      <li>Community assistance programs</li>
    </ul>

    <p>Part of lapse prevention is knowing these resources and helping clients access them.</p>

    <h2>After a Lapse: Reinstatement</h2>

    <p>If prevention fails, reinstatement may be possible:</p>

    <h3>Within Grace Period</h3>
    <ul>
      <li>Pay past-due amount to reinstate immediately</li>
      <li>Coverage continuous as if lapse never happened</li>
    </ul>

    <h3>After Termination</h3>
    <ul>
      <li>Some plans allow late reinstatement with conditions</li>
      <li>May need to wait for next enrollment period</li>
      <li>Original Medicare available immediately</li>
      <li>Help client get covered appropriately</li>
    </ul>

    <h2>Lapse Prevention Metrics</h2>

    <p>Track your lapse prevention effectiveness:</p>

    <ul>
      <li><strong>Lapse rate:</strong> Percentage of clients with coverage gaps</li>
      <li><strong>Detection rate:</strong> Did you catch risks before lapse?</li>
      <li><strong>Intervention success:</strong> Of clients at risk, how many saved?</li>
      <li><strong>Root causes:</strong> Why did lapses happen (for those that did)?</li>
    </ul>

    <p>Aim for: High detection rate, high intervention success, very low lapse rate.</p>
  `,

  faqs: [
    {
      question: "What warning signs indicate a client might lapse?",
      answer: "Direct signals: grace period entry, premium assistance inquiries, returned mail. Indirect signals: extended non-response to contact, financial stress mentioned, major life events (hospitalization, spouse death), cognitive concerns, caregiver changes."
    },
    {
      question: "How do I help a client who can't afford their premium?",
      answer: "Know the assistance programs: Medicare Savings Programs (QMB, SLMB, QI) pay Part A/B premiums. Low-Income Subsidy (Extra Help) helps with Part D costs. SHIP and AAA have local resources. Many clients qualify but don't know these exist."
    },
    {
      question: "What should I say when I discover a client is in grace period?",
      answer: "Be direct but supportive: 'Your coverage is in a grace period—you have [X] days before it terminates. Let's figure out what's going on and how to fix it. Is it a payment issue? Something else? I want to help you stay protected.'"
    },
    {
      question: "Can coverage be reinstated after termination?",
      answer: "Sometimes. Within grace period: pay past-due amount for immediate reinstatement. After termination: some plans allow late reinstatement with conditions, otherwise client may need to wait for next enrollment period. Original Medicare is always available."
    },
    {
      question: "How do I prevent lapses proactively?",
      answer: "Set up early warning monitoring, know your clients' situations (financial stress, life events), encourage auto-pay enrollment, verify contact information stays current, check in when there's extended silence, and know assistance programs to refer when needed."
    }
  ],

  sources: [
    {
      title: "Medicare Premium Payment Information",
      url: "https://www.cms.gov/Medicare/Eligibility-and-Enrollment/MedicareEligEnrol",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    },
    {
      title: "Medicare Savings Programs",
      url: "https://www.medicare.gov/your-medicare-costs/get-help-paying-costs/medicare-savings-programs",
      publisher: "Medicare.gov",
      accessedDate: "2024-12-10"
    }
  ]
};

export default content;
